import { message } from "antd";
import { useCallback, useEffect, useState } from "react";

import { getMemberDetail, updateMemberRemark, updateMemberTags } from "@/api/member";
import type { OpsMemberDetailQueryRequest, OpsMemberDetailVO } from "@/api/member/type";

/** 账户明细默认每页条数 */
const DEFAULT_ACCOUNT_LOG_PAGE_SIZE = 10;

/**
 * 会员详情：拉取详情、账户流水分页、备注/标签更新
 * @param memberId 会员 ID；空则不请求
 * @param open 弹窗是否打开
 */
export const useMemberDetail = (memberId: string | null, open: boolean) => {
  /** 详情数据 */
  const [detail, setDetail] = useState<OpsMemberDetailVO | null>(null);
  /** 详情加载中 */
  const [loading, setLoading] = useState(false);
  /** 备注保存中 */
  const [remarkSaving, setRemarkSaving] = useState(false);
  /** 标签保存中 */
  const [tagSaving, setTagSaving] = useState(false);
  /** 账户明细查询参数 */
  const [logQuery, setLogQuery] = useState<OpsMemberDetailQueryRequest>({
    accountLogPageNum: 1,
    accountLogPageSize: DEFAULT_ACCOUNT_LOG_PAGE_SIZE,
  });

  /** 拉取会员详情 */
  const fetchDetail = useCallback(async (id: string, params: OpsMemberDetailQueryRequest) => {
    setLoading(true);
    try {
      const res = await getMemberDetail(id, params);
      setDetail(res.data ?? null);
    } catch {
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // 弹窗打开且有会员 ID 时拉详情
  useEffect(() => {
    if (!open || !memberId) {
      setDetail(null);
      return;
    }
    void fetchDetail(memberId, logQuery);
  }, [open, memberId, logQuery, fetchDetail]);

  // 关闭时重置账户明细分页
  useEffect(() => {
    if (open) return;
    setLogQuery({
      accountLogPageNum: 1,
      accountLogPageSize: DEFAULT_ACCOUNT_LOG_PAGE_SIZE,
    });
  }, [open]);

  /** 账户明细翻页 */
  const handleAccountLogPageChange = useCallback((pageNum: number, pageSize: number) => {
    setLogQuery((prev) => ({
      ...prev,
      accountLogPageNum: pageNum,
      accountLogPageSize: pageSize,
    }));
  }, []);

  /** 保存备注并刷新详情 */
  const saveRemark = useCallback(
    async (remark: string) => {
      if (!memberId) return false;
      setRemarkSaving(true);
      try {
        await updateMemberRemark(memberId, { remark });
        message.success("备注已更新");
        await fetchDetail(memberId, logQuery);
        return true;
      } catch {
        return false;
      } finally {
        setRemarkSaving(false);
      }
    },
    [memberId, logQuery, fetchDetail],
  );

  /** 追加会员标签 */
  const addTag = useCallback(
    async (tag: string) => {
      if (!memberId) return false;
      // 避免重复追加已有标签
      if (detail?.memberTags?.includes(tag)) {
        message.warning("标签已存在");
        return false;
      }
      setTagSaving(true);
      try {
        await updateMemberTags(memberId, { addTags: [tag] });
        message.success("标签已添加");
        await fetchDetail(memberId, logQuery);
        return true;
      } catch {
        return false;
      } finally {
        setTagSaving(false);
      }
    },
    [memberId, detail?.memberTags, logQuery, fetchDetail],
  );

  /** 移除会员标签 */
  const removeTag = useCallback(
    async (tag: string) => {
      if (!memberId) return false;
      setTagSaving(true);
      try {
        await updateMemberTags(memberId, { removeTags: [tag] });
        message.success("标签已移除");
        await fetchDetail(memberId, logQuery);
        return true;
      } catch {
        return false;
      } finally {
        setTagSaving(false);
      }
    },
    [memberId, logQuery, fetchDetail],
  );

  return {
    detail,
    loading,
    remarkSaving,
    tagSaving,
    handleAccountLogPageChange,
    saveRemark,
    addTag,
    removeTag,
  };
};
